import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Skeleton, Button } from 'antd';
import { capitalCase } from 'change-case';
import { snakeCase, without } from 'lodash';
import moment from 'moment';
import { useBundle } from '@amzn/react-arb-tools';
import AssignWorkOrderTaskModal from 'components/AssignWorkOrderTaskModal';
import { PageContent } from '@xbcb/display-components';
import CompleteWorkOrderTaskModal from 'components/CompleteWorkOrderTaskModal';
import RightContentSpace from 'components/RightContentSpace';
import WorkOrderInfoCard, { WorkOrderInfo } from 'components/WorkOrderInfoCard';
import WorkOrderTabs from 'components/WorkOrderTabs';
import {
  useCurrentUser,
  useQueryParams,
  useWorkOrderTaskType,
  QueryString,
} from 'libs/hooks';
import {
  WorkOrderTaskStatus,
  WorkOrderTaskType,
  WorkOrderBadge,
} from '@xbcb/work-order-types';
import { StyledResult } from './styles';
import { AccountType, RecordType } from '@xbcb/shared-types';
import {
  getKeyForWorkOrderTabConfiguration,
  getWorkOrderTabConfiguration,
  DEFAULT,
} from 'libs/workOrderTabConfiguration';
import { WorkOrderTab } from '@xbcb/ui-types';
import { AppRecordProvidedProps } from 'routes';
import { checkAccess, UserPermissions } from '@xbcb/client-utils';
import CancelWorkOrderModal from 'components/CancelWorkOrderModal';
import { WorkOrderTaskEvaluatedState } from 'libs/evaluateWorkOrderTaskState';
import ResolveWorkOrderTaskEscalationModal from 'components/ResolveWorkOrderTaskEscalationModal';
import EscalationMemo from '../EscalationMemo';
import { getCountryAbbreviationFromCustomsBrokerId } from '@xbcb/core';
import { euCountries } from 'types/euCountriesList';
import EscalateWorkOrderTaskModal from 'components/EscalateWorkOrderTask/component';
import CreateExceptionTaskModal from 'components/CreateExceptionTask';
import { reportError, safeGetMessage } from '@xbcb/ui-utils';
import { isBrexitEnabledForBroker } from '@xbcb/party-utils';
import { getImportCountryCodeForCuDe } from 'libs/getImportCountryCodeForCuDe';
import { CustomsDeclaration } from '__generated__/graphql';
import {
  isProject42Enabled,
  isAssignmentWorkspaceFeatureEnabled,
} from 'featureFlags';

const classificationBadges = [
  WorkOrderBadge.BIXD,
  WorkOrderBadge.CBAM,
  WorkOrderBadge.MULTIVAT,
  WorkOrderBadge.DG,
];

const euUkRecordTypes = [
  RecordType.FR_CUSTOMS_ENTRY,
  RecordType.DE_CUSTOMS_ENTRY,
  RecordType.GB_CUSTOMS_ENTRY,
  RecordType.NL_CUSTOMS_ENTRY,
  RecordType.CUSTOMS_DECLARATION,
];

export type WorkOrderProps = AppRecordProvidedProps & {
  loading?: boolean;
  record?: any;
  recordType?: RecordType;
  // returns additional data to display in the "Work Order Info" card
  getAdditionalWorkOrderInfo?: ({
    record,
  }: {
    record?: any;
  }) => WorkOrderInfo[];
  // returns additional cards to display below the "Work Order Info" card
  getAdditionalInfoCards?: ({ record }: { record?: any }) => React.ReactNode[];
  disableCancel?: boolean;
  children?: React.ReactNode;
};

// These are the props injected by the WorkOrder higher-order component
export type WorkOrderProvidedProps = {
  getTabs: () => WorkOrderTab[];
  tabs: WorkOrderTab[];
  tasks: any[];
  activeTab: QueryString;
  setActiveTab: (value: string) => void;
  taskEvaluatedState?: WorkOrderTaskEvaluatedState;
};

// These are the total props provided to the Work Order child components, the sum of AppRecord's and WorkOrder's injected props.
export type WorkOrderRecordProps = WorkOrderProvidedProps &
  AppRecordProvidedProps;

// N.B. WorkOrder gets some of its props injected by AppRecord(Create)
const WorkOrder: React.FC<WorkOrderProps> = (props) => {
  const [workOrderBundle] = useBundle('components.WorkOrder');
  const [workOrderStatusBundle] = useBundle(
    'types.workOrder.enums.WorkOrderStatus',
  );
  const [manageAnalysisBadgesBundle] = useBundle(
    'components.ManageAnalysisBadges',
  );
  const {
    children,
    getAdditionalInfoCards,
    getAdditionalWorkOrderInfo,
    ...threadedPropsFromAppRecord // AppRecord gives WorkOrder props, WorkOrder passes them down to its child component.
  } = props;
  const { loading, record, recordType } = threadedPropsFromAppRecord;
  const history = useHistory();
  const currentUser = useCurrentUser();
  const customsBrokerId = currentUser.customsBroker?.id;
  const countryOfOperation =
    customsBrokerId &&
    getCountryAbbreviationFromCustomsBrokerId(customsBrokerId);
  const isEnabledInEU = euCountries.includes(countryOfOperation);

  const showAssignmentWorkspaceTableButton =
    isBrexitEnabledForBroker(currentUser.customsBroker) ||
    // TODO: remove operator from context
    isAssignmentWorkspaceFeatureEnabled({
      operator: isEnabledInEU ? currentUser.id : undefined,
    });

  const additionalInfoCards = getAdditionalInfoCards?.({ record }) || [];
  const additionalWorkOrderInfo =
    getAdditionalWorkOrderInfo?.({ record }) || [];

  const {
    deadline,
    milestones = [],
    status = '',
    tasks = [],
    pgaDispositions = [],
    confirmations,
    group = {},
    inquiries = [],
  } = record || {};

  const shipperId = currentUser.account?.id;
  const isP42 = isProject42Enabled({ shipperId });

  const {
    taskType: workOrderTaskType,
    setTaskType: setWorkOrderTaskType,
    taskId,
    setTaskId,
  } = useWorkOrderTaskType();
  const [activeTab, setActiveTab] = useQueryParams('tab');

  // Get current task - prefer taskId over taskType for specificity
  const getCurrentTask = () => {
    if (taskId) {
      const task = tasks.find((task: { id: string }) => task.id === taskId);
      // Auto-set taskType if taskId is valid but taskType is missing
      if (task && !workOrderTaskType) {
        setWorkOrderTaskType(task.definition.workOrderTaskType);
      }
      return task;
    }
    if (workOrderTaskType) {
      return tasks.find(
        (task: { definition: { workOrderTaskType: string } }) =>
          task.definition.workOrderTaskType === workOrderTaskType,
      );
    }
    return null;
  };

  const currentTask = getCurrentTask();

  // NOTE: In P42, we do not render the WorkOrderTabs component, which handles
  // the milestone and task widget on the right hand side of the screen. However,
  // the WorkOrderTabs component sets the work order task evaluated status to
  // STARTED by default, which enables edits to the form fields. Without the
  // STARTED status, all fields in the form are disabled and readonly. We set
  // the default task state for the P42 entry filing work order below to get
  // around having to render the WorkOrderTabs component.
  const [taskEvaluatedState, setTaskEvaluatedState] = useState<
    WorkOrderTaskEvaluatedState | undefined
  >(isP42 ? WorkOrderTaskEvaluatedState.STARTED : undefined);

  if (
    (workOrderTaskType &&
      !tasks.some(
        ({ definition }: { definition: { workOrderTaskType: string } }) =>
          definition.workOrderTaskType === workOrderTaskType,
      )) ||
    (taskId && !tasks.some((task: { id: string }) => task.id === taskId))
  ) {
    // workOrderTaskType is set in the query string, but it's not valid (not one of the tasks types defined in record.tasks). Let's remove it (and display the overall WorkOrder view)
    // setWorkOrderTaskType(
    //   WorkOrderTaskType.US_CONSUMPTION_ENTRY_DOCUMENT_TAGGING,
    // );
    // console.log(
    //   'SET WORK ORDER TASK TYPE TO BE ' +
    //     WorkOrderTaskType.US_CONSUMPTION_ENTRY_DOCUMENT_TAGGING,
    // );
    setWorkOrderTaskType(undefined);
    setTaskId(undefined);
  }

  // For use cases where you need to re-compute tabs based on new form data.
  const getTabs = () => {
    const importCountryCode = getImportCountryCodeForCuDe(
      record as CustomsDeclaration,
    );
    const keyForTabConfiguration = getKeyForWorkOrderTabConfiguration(
      recordType,
      group?.forwarders,
      importCountryCode,
    );
    if (recordType && keyForTabConfiguration) {
      const tabConfiguration = getWorkOrderTabConfiguration(
        recordType,
        keyForTabConfiguration,
        importCountryCode,
      );
      const taskTypeKeyForTabConfiguration = (workOrderTaskType ||
        DEFAULT) as keyof typeof tabConfiguration;

      let tabs =
        tabConfiguration[taskTypeKeyForTabConfiguration] ||
        tabConfiguration[DEFAULT];

      if (
        recordType === RecordType.US_CONSUMPTION_ENTRY &&
        taskTypeKeyForTabConfiguration === DEFAULT &&
        isP42
      ) {
        return [
          WorkOrderTab.Transportation,
          WorkOrderTab.CommercialInvoices,
          WorkOrderTab.Documents,
        ];
      }

      if (
        !currentUser.loading &&
        !checkAccess(currentUser, RecordType.DOCUMENT, UserPermissions.READ)
      ) {
        tabs = without(tabs, WorkOrderTab.Documents);
      }
      if (
        !currentUser.loading &&
        currentUser.accountType !== AccountType.OPERATOR
      ) {
        // Only operator users should see the `Tasks` and `Bond` tabs
        tabs = without(tabs, WorkOrderTab.Tasks);
        tabs = without(tabs, WorkOrderTab.Bond);
        tabs = without(tabs, WorkOrderTab.CommercialInvoices);
      }
      return tabs;
    } else {
      throw new Error(
        `No work order tab configuration found for ${recordType}`,
      );
    }
  };

  const tabs = getTabs();

  if (!tabs.includes(activeTab as WorkOrderTab)) {
    try {
      // if the activeTab in the query string isn't one of the available tabs computed for this page, send them to the first available tab.
      setActiveTab(tabs[0]);
    } catch (e) {
      reportError(e);
      throw e;
    }
  }

  const formattedDeadline = deadline
    ? moment(deadline).format('MM/DD/YYYY hh:mm a')
    : 'N/A';

  const workOrderTask = currentTask;

  const handleAssignmentsRedirect = () => history.push('/assignments');
  const handleAssignmentWorkspaceRedirect = () =>
    history.push('/assignment-workspace');

  const assignmentTableButton = (
    <Button
      type="primary"
      key="assignments"
      onClick={handleAssignmentsRedirect}
    >
      {safeGetMessage(workOrderBundle, 'go_to_assignments')}
    </Button>
  );

  const assignmentWorkspaceTableButton = (
    <Button
      type="primary"
      key="assignmentsWorkspace"
      onClick={handleAssignmentWorkspaceRedirect}
    >
      {safeGetMessage(workOrderBundle, 'go_to_assignment_workspace')}
    </Button>
  );

  const analysisBadgesInfo = () => {
    if (
      !recordType ||
      !(euUkRecordTypes as RecordType[]).includes(recordType)
    ) {
      return [];
    }
    const analysisBadges =
      record?.badges?.filter((badge: WorkOrderBadge) =>
        classificationBadges.includes(badge),
      ) || [];
    return [
      {
        label: safeGetMessage(manageAnalysisBadgesBundle, 'analysis_badges'),
        value: analysisBadges.length > 0 ? analysisBadges.join(', ') : 'None',
      },
    ];
  };
  const regulatoryReadyDateInfo = () => {
    if (recordType !== RecordType.US_CONSUMPTION_ENTRY) {
      return [];
    }
    return [
      {
        label: 'Regulatory ready date',
        value: record?.regulatoryReadyDate
          ? moment(record.regulatoryReadyDate).format('MM/DD/YYYY hh:mm a')
          : 'N/A',
      },
    ];
  };

  const workOrderInfo = [
    {
      label: safeGetMessage(workOrderBundle, 'deadline'),
      value: formattedDeadline,
    },
    {
      label: safeGetMessage(workOrderBundle, 'work_order_status'),
      value: safeGetMessage(
        workOrderStatusBundle,
        snakeCase(status).toLowerCase(),
        undefined,
        capitalCase(status),
      ),
    },
    ...analysisBadgesInfo(),
    ...regulatoryReadyDateInfo(),
    ...additionalWorkOrderInfo,
  ];

  const infoCards = [
    <WorkOrderInfoCard loading={loading} workOrderInfo={workOrderInfo} />,
    ...additionalInfoCards,
  ];

  // TODO we use this cloning logic here, in AppRecord, and in AppRecordCreate. Consolidate.
  const childrenWithProps = React.Children.map(
    React.Children.only(children), // util method that ensures only one child is passed.
    (child) => {
      if (React.isValidElement(child)) {
        // These are all the additional props that `WorkOrder` provides to the component it is rendering (the child component passed in)
        const providedProps: WorkOrderRecordProps = {
          getTabs,
          tabs,
          tasks,
          activeTab,
          setActiveTab,
          taskEvaluatedState,
          ...threadedPropsFromAppRecord,
        };
        return React.cloneElement(child, providedProps);
      } else {
        return child;
      }
    },
  );

  const isCompleted = workOrderTask?.status === WorkOrderTaskStatus.COMPLETED;
  const isCanceled = workOrderTask?.status === WorkOrderTaskStatus.CANCELED;

  return (
    <>
      <AssignWorkOrderTaskModal record={record} />
      <CancelWorkOrderModal record={record} />
      <CompleteWorkOrderTaskModal taskStatus={workOrderTask?.status} />
      <EscalateWorkOrderTaskModal recordId={record.id} />
      <ResolveWorkOrderTaskEscalationModal
        currentUser={currentUser}
        record={record}
        workOrderTaskType={workOrderTaskType as WorkOrderTaskType}
        workOrderTasks={tasks}
      />
      <CreateExceptionTaskModal record={record} />
      <PageContent
        rightContent={
          !isP42 && (
            <RightContentSpace>
              <EscalationMemo
                tasks={tasks}
                workOrderTaskType={workOrderTaskType}
                currentUser={currentUser}
                record={record}
              />
              <WorkOrderTabs
                record={record}
                recordType={recordType}
                additionalInfoCards={infoCards}
                loading={loading}
                milestones={milestones}
                confirmations={confirmations}
                pgaDispositions={pgaDispositions}
                taskId={workOrderTask?.id}
                inquiries={inquiries}
                setTaskEvaluatedState={setTaskEvaluatedState}
              />
            </RightContentSpace>
          )
        }
      >
        {loading ? (
          <Skeleton active />
        ) : isCompleted ? (
          <StyledResult
            status="success"
            title={safeGetMessage(workOrderBundle, 'task_completed')}
            extra={
              showAssignmentWorkspaceTableButton
                ? assignmentWorkspaceTableButton
                : assignmentTableButton
            }
          />
        ) : isCanceled ? (
          <StyledResult
            status="error"
            title={safeGetMessage(workOrderBundle, 'task_canceled')}
            extra={
              showAssignmentWorkspaceTableButton
                ? assignmentWorkspaceTableButton
                : assignmentTableButton
            }
          />
        ) : (
          childrenWithProps
        )}
      </PageContent>
    </>
  );
};

export default WorkOrder;
